export type CacheScope = 'app' | 'user' | 'userOrg';

export type CacheEnvelope<T> = {
  writtenAt: number;
  expiresAt: number;
  value: T;
};

export type CacheStorage = 'localStorage' | 'sessionStorage';

// Subject and organization are structurally required for the scopes that key
// on them: there is deliberately no ambient fallback here. A null value means
// "no identity, so no cache key" (reads and writes become no-ops); omitting
// the field entirely is a compile error. Callers pass the identity they
// captured before their request, so a session or organization switch mid-save
// can never file a value under the new identity's key.
export type CacheKeyOptions =
  | { scope: 'app'; namespace: string; qualifier?: string | number }
  | { scope: 'user'; namespace: string; qualifier?: string | number; subject: string | null }
  | { scope: 'userOrg'; namespace: string; qualifier?: string | number; subject: string | null; organization: string | null };

export type CacheOptions = CacheKeyOptions & {
  storage: CacheStorage;
  allowExpired?: boolean;
  validate?: (value: unknown) => boolean;
};

// Namespaces exist only for surfaces that actually read/write the envelope
// cache. Tenant data belongs in userOrg entries; the startup/account-boundary
// purge removes the older subjectless appdata copies without adopting them.
export const CACHE_NAMESPACES = {
  errorCounts: 'error-counts',
  recurringImportHeaders: 'recurring-import-headers',
  organizationTimezone: 'organization-timezone',
  importPreview: 'import-preview',
  integrationUnhealthy: 'integration-unhealthy',
  integrationWarehouses: 'integration-warehouses',
  jsonBuilderOptions: 'json-builder-options',
} as const;

export const CACHE_TTLS = {
  errorCounts: 2 * 60 * 1000,
  organizationTimezone: 60 * 60 * 1000,
  recurringImportHeaders: 20 * 60 * 1000,
  integrationUnhealthy: 2 * 60 * 1000,
  integrationWarehouses: 60 * 1000,
  jsonBuilderOptions: 2 * 60 * 1000,
} as const;

// Retention deadline for stale-while-revalidate caches. Consumers that read
// with allowExpired enforce freshness themselves (e.g. the warehouses
// refreshedAt check), so their expiresAt must be a retention bound the
// sweeper honors — not the freshness TTL — or a page load one minute after
// the write deletes the stale copy the flag exists to serve.
export const CACHE_SWR_RETENTION_MS = 24 * 60 * 60 * 1000;

const CACHE_PREFIX = 'astur:v1:';
const LEGACY_IMPORT_PREVIEW_PREFIX = 'import-preview-mappings:v1:';
// Mirror entries are keyed by storage kind + key so the same cache key in
// localStorage and sessionStorage cannot shadow each other.
type MemoryMirrorEntry = {
  raw: string;
  parsed: CacheEnvelope<unknown>;
};
const memoryMirror = new Map<string, MemoryMirrorEntry>();
const mirrorKey = (storage: CacheStorage, key: string): string => `${storage}|${key}`;
// Listeners receive the changed cache key, or null when the change is broad
// (multi-key clear) — subscribers should treat null as "anything may have changed".
type CacheListener = (changedKey: string | null) => void;
const cacheListeners = new Set<CacheListener>();
let storageListenerInstalled = false;

const notifyCacheListeners = (changedKey: string | null): void => {
  // Deferred so cache reads/writes that happen during render (lazy state
  // initializers, memos) can never trigger subscriber setState mid-render.
  queueMicrotask(() => {
    for (const listener of cacheListeners) listener(changedKey);
  });
};

export const subscribeCacheChanges = (listener: CacheListener): (() => void) => {
  cacheListeners.add(listener);
  return () => cacheListeners.delete(listener);
};

export const buildCacheKey = (options: CacheKeyOptions): string | null => {
  const namespace = encodeURIComponent(options.namespace);
  const qualifier = options.qualifier === undefined ? '' : `:${encodeURIComponent(String(options.qualifier))}`;

  if (options.scope === 'app') {
    return `${CACHE_PREFIX}app:${namespace}${qualifier}`;
  }

  if (!options.subject) return null;

  if (options.scope === 'user') {
    return `${CACHE_PREFIX}user:${encodeURIComponent(options.subject)}:${namespace}${qualifier}`;
  }

  if (!options.organization) return null;
  return `${CACHE_PREFIX}user-org:${encodeURIComponent(options.subject)}:${encodeURIComponent(options.organization)}:${namespace}${qualifier}`;
};

const getStorage = (storage: CacheStorage): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window[storage];
  } catch {
    // Storage access can throw in privacy-restricted browsing contexts.
    return null;
  }
};

export const sweepExpiredCache = (storage: CacheStorage): void => {
  const store = getStorage(storage);
  if (!store) return;

  const now = Date.now();
  const expiredKeys: string[] = [];
  try {
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (!key) continue;
      // Filter on the key before materializing the value: unrelated entries
      // (the appdata blob, Keycloak state, third-party keys) must not be read
      // into memory on every page load.
      const isEnvelopeKey = key.startsWith(CACHE_PREFIX);
      const isLegacyPreviewKey = storage === 'localStorage' && key.startsWith(LEGACY_IMPORT_PREVIEW_PREFIX);
      if (!isEnvelopeKey && !isLegacyPreviewKey) continue;
      const raw = store.getItem(key);
      if (!raw) continue;
      try {
        if (isEnvelopeKey) {
          const envelope = JSON.parse(raw) as Partial<CacheEnvelope<unknown>>;
          if (!Number.isFinite(envelope?.expiresAt) || (envelope.expiresAt as number) <= now) {
            expiredKeys.push(key);
          }
        } else {
          const legacyDraft = JSON.parse(raw) as { expiresAt?: unknown };
          if (typeof legacyDraft?.expiresAt === 'string') {
            const expiresAt = new Date(legacyDraft.expiresAt).getTime();
            if (!Number.isNaN(expiresAt) && expiresAt <= now) expiredKeys.push(key);
          }
        }
      } catch {
        // Unparseable entries are unusable regardless of shape — legacy
        // import-preview drafts included; retaining corrupt JSON forever just
        // leaks storage.
        expiredKeys.push(key);
      }
    }
  } catch {
    // Enumeration and reads can throw in privacy-restricted contexts.
    return;
  }
  for (const key of expiredKeys) {
    memoryMirror.delete(mirrorKey(storage, key));
    try {
      store.removeItem(key);
    } catch {
      // Storage may become unavailable after it was resolved.
    }
  }
  if (expiredKeys.length > 0) notifyCacheListeners(null);
};

const handleStorageEvent = (event: StorageEvent): void => {
  if (event.storageArea !== window.localStorage) return;
  if (event.key === null) {
    memoryMirror.clear();
  } else {
    memoryMirror.delete(mirrorKey('localStorage', event.key));
  }
  notifyCacheListeners(event.key);
};

const installStorageListener = (): void => {
  if (storageListenerInstalled || typeof window === 'undefined') return;
  storageListenerInstalled = true;
  window.addEventListener('storage', handleStorageEvent);
};

const removeByKey = (key: string, storage: CacheStorage): void => {
  memoryMirror.delete(mirrorKey(storage, key));
  try {
    getStorage(storage)?.removeItem(key);
  } catch {
    // Storage access is best-effort; the in-memory entry is already gone.
  }
  notifyCacheListeners(key);
};

// Pure read for useSyncExternalStore getSnapshot callers: never removes
// entries, notifies listeners, or installs the storage listener — a snapshot
// that removes a corrupt entry would fire notifications mid-render. Malformed
// or invalid entries simply read as null; cleanup belongs to readCache and
// sweepExpiredCache, which run outside render. The memory mirror is still
// (idempotently) populated so repeated snapshots of an unchanged raw string
// return an identical reference.
export const peekCache = <T>(options: CacheOptions): T | null => {
  const key = buildCacheKey(options);
  const storage = getStorage(options.storage);
  if (!key || !storage) return null;

  const cached = memoryMirror.get(mirrorKey(options.storage, key));
  let raw: string | null;
  try {
    raw = cached?.raw ?? storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const envelope = (cached?.raw === raw ? cached.parsed : JSON.parse(raw)) as Partial<CacheEnvelope<T>>;
    if (
      typeof envelope !== 'object' ||
      envelope === null ||
      !Number.isFinite(envelope.writtenAt) ||
      !Number.isFinite(envelope.expiresAt) ||
      !Object.prototype.hasOwnProperty.call(envelope, 'value')
    ) {
      return null;
    }

    memoryMirror.set(mirrorKey(options.storage, key), {
      raw,
      parsed: envelope as CacheEnvelope<unknown>,
    });

    if ((envelope.expiresAt as number) <= Date.now() && !options.allowExpired) return null;
    if (options.validate && !options.validate(envelope.value)) return null;
    return envelope.value as T;
  } catch {
    return null;
  }
};

export const readCache = <T>(options: CacheOptions): T | null => {
  const key = buildCacheKey(options);
  const storage = getStorage(options.storage);
  if (!key || !storage) return null;
  installStorageListener();

  const cached = memoryMirror.get(mirrorKey(options.storage, key));
  let raw: string | null;
  try {
    raw = cached?.raw ?? storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const envelope = (cached?.raw === raw ? cached.parsed : JSON.parse(raw)) as Partial<CacheEnvelope<T>>;
    if (
      typeof envelope !== 'object' ||
      envelope === null ||
      !Number.isFinite(envelope.writtenAt) ||
      !Number.isFinite(envelope.expiresAt) ||
      !Object.prototype.hasOwnProperty.call(envelope, 'value')
    ) {
      removeByKey(key, options.storage);
      return null;
    }

    memoryMirror.set(mirrorKey(options.storage, key), {
      raw,
      parsed: envelope as CacheEnvelope<unknown>,
    });

    if ((envelope.expiresAt as number) <= Date.now()) {
      if (!options.allowExpired) {
        removeByKey(key, options.storage);
        return null;
      }
    }

    if (options.validate && !options.validate(envelope.value)) {
      removeByKey(key, options.storage);
      return null;
    }

    return envelope.value as T;
  } catch {
    removeByKey(key, options.storage);
    return null;
  }
};

// Returns whether the value was durably persisted to storage. Callers that
// delete a fallback copy after migrating ("write then remove the old key")
// must gate the removal on this — a false return means the value lives only
// in this tab's memory mirror.
export const writeCache = <T>(options: CacheOptions & { value: T; expiresAt: number }): boolean => {
  if (!Number.isFinite(options.expiresAt)) return false;
  const key = buildCacheKey(options);
  const storage = getStorage(options.storage);
  if (!key || !storage) return false;
  installStorageListener();

  const envelope: CacheEnvelope<T> = {
    writtenAt: Date.now(),
    expiresAt: options.expiresAt,
    value: options.value,
  };
  let raw: string;
  try {
    raw = JSON.stringify(envelope);
  } catch {
    // A circular value or throwing toJSON must fail the write, not escape
    // into the calling effect.
    return false;
  }
  memoryMirror.set(mirrorKey(options.storage, key), {
    raw,
    parsed: envelope as CacheEnvelope<unknown>,
  });
  let persisted = true;
  try {
    storage.setItem(key, raw);
  } catch {
    // Quota exceeded (or storage denied): sweep expired entries and retry once.
    sweepExpiredCache(options.storage);
    try {
      storage.setItem(key, raw);
    } catch {
      // Persist best-effort only; the memory mirror still serves this tab.
      persisted = false;
    }
  }
  notifyCacheListeners(key);
  return persisted;
};

export const removeCache = (options: CacheOptions): void => {
  const key = buildCacheKey(options);
  if (key) removeByKey(key, options.storage);
};

// Import-preview entries are unsaved user drafts, not re-fetchable caches;
// they survive subject-scoped clears (e.g. logout) so a user can resume a
// draft after signing back in. Envelope keys are subject-scoped, so another
// subject can never read them; pre-session legacy draft keys carry no
// subject and stay readable until a durable write migrates them.
const isImportPreviewKey = (key: string): boolean => {
  const segments = key.split(':');
  const namespaceIndex = segments[2] === 'user-org' ? 5 : 4;
  return segments[namespaceIndex] === encodeURIComponent(CACHE_NAMESPACES.importPreview);
};

const clearSubjectFromStorage = (storageName: CacheStorage, subject: string): void => {
  const encodedSubject = encodeURIComponent(subject);
  const prefixes = [`${CACHE_PREFIX}user:${encodedSubject}:`, `${CACHE_PREFIX}user-org:${encodedSubject}:`];
  const mirrorPrefix = `${storageName}|`;

  for (const cachedKey of memoryMirror.keys()) {
    if (!cachedKey.startsWith(mirrorPrefix)) continue;
    const key = cachedKey.slice(mirrorPrefix.length);
    if (prefixes.some((prefix) => key.startsWith(prefix)) && !isImportPreviewKey(key)) {
      memoryMirror.delete(cachedKey);
    }
  }

  const storage = getStorage(storageName);
  if (!storage) return;

  // A throwing storage must not abort the rest of sign-out cleanup (the
  // sibling storage's clear, session ETags, validated-organization resets).
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && prefixes.some((prefix) => key.startsWith(prefix)) && !isImportPreviewKey(key)) keys.push(key);
    }
    for (const key of keys) {
      memoryMirror.delete(mirrorKey(storageName, key));
      storage.removeItem(key);
    }
  } catch {
    // Best-effort: the memory-mirror sweep above already ran.
  }
};

export const clearUserScopedCaches = (subject: string): void => {
  if (typeof window === 'undefined' || !subject) return;
  clearSubjectFromStorage('localStorage', subject);
  clearSubjectFromStorage('sessionStorage', subject);
  notifyCacheListeners(null);
};

// Test-only reset used by the shared unit-test teardown: the mirror is a
// module singleton, so worker-shared test files must be able to drop residue.
export const __resetCacheMemoryMirrorForTests = (): void => {
  memoryMirror.clear();
};

export const __resetCacheListenersForTests = (): void => {
  cacheListeners.clear();
};

// Detaches the cross-tab storage listener and restores the "not installed"
// flag. Resetting the flag alone would let the next install add a duplicate
// listener, so the removal and the flag reset must stay together.
export const __resetCacheStorageListenerForTests = (): void => {
  if (typeof window !== 'undefined') window.removeEventListener('storage', handleStorageEvent);
  storageListenerInstalled = false;
};

// Storage keys written by the pre-session cache helpers; superseded by the
// astur:v1:* envelopes above. Swept once per page load so abandoned entries
// do not accumulate against the storage quota forever. Only re-fetchable
// caches belong here — import-preview drafts are user work and are instead
// kept readable by importPreviewStorage until a durable write migrates them.
const LEGACY_CACHE_KEY_PREFIXES = ['org_timezone_', 'recurring-import-headers:'];
let legacyCachesPurged = false;

export const __resetLegacyCachePurgeForTests = (): void => {
  legacyCachesPurged = false;
};

export const purgeLegacyCaches = (): void => {
  if (legacyCachesPurged || typeof window === 'undefined') return;
  legacyCachesPurged = true;

  for (const storageName of ['localStorage', 'sessionStorage'] as const) {
    const store = getStorage(storageName);
    if (!store) continue;
    try {
      const keys: string[] = [];
      for (let index = 0; index < store.length; index += 1) {
        const key = store.key(index);
        if (key && LEGACY_CACHE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) keys.push(key);
      }
      for (const key of keys) store.removeItem(key);
    } catch {
      // Enumeration and removal can throw in privacy-restricted contexts;
      // a failed purge must not abort startup cache maintenance.
    }
  }
};

const isLegacyAppDataTenantCacheKey = (key: string): boolean =>
  key === 'options_cache' || key.endsWith('_hasUnhealthy') || key.endsWith('_hasUnhealthyExpire') || key.includes('_warehouses_list');

export const purgeLegacyAppDataTenantCaches = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem('appdata');
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
    const next = { ...(parsed as Record<string, unknown>) };
    let changed = false;
    for (const key of Object.keys(next)) {
      if (!isLegacyAppDataTenantCacheKey(key)) continue;
      delete next[key];
      changed = true;
    }
    if (changed) window.localStorage.setItem('appdata', JSON.stringify(next));
  } catch {
    // Corrupt appdata remains the AppdataContext's recovery concern.
  }
};
